import {
    Dispatch,
    ReactElement,
    ReactNode,
    SetStateAction,
    createContext,
    useMemo,
    useState,
} from "react"

import { IInviteDetails } from "../../../services/middleware/types/account/pendingInvite"
import {
    FIRST_LOGIN_INITIAL,
    FirstLoginDispatcherKeys,
    INVITE_INITIAL,
} from "../screenDispatchers/firstLoginConstants"

export type SetCurrentScreen = Dispatch<
    SetStateAction<FirstLoginDispatcherKeys>
>

export interface IAccountSetUpContextValue {
    currentScreen: FirstLoginDispatcherKeys
    setCurrentScreen: SetCurrentScreen
    canClaimDomain: boolean
    isInviteMode: boolean
    invite?: IInviteDetails
}

export const AccountSetUpContext = createContext(
    {} as IAccountSetUpContextValue
)

interface IAccountSetUpProviderProps {
    isInviteMode: boolean
    canClaimDomain: boolean
    children: ReactNode
    invite?: IInviteDetails
}

/**
 * Context provider for the screens the user sees when they first log in, or
 * when they have pending invites. It provides navigation between these screens.
 */
const AccountSetUpProvider = ({
    isInviteMode,
    canClaimDomain,
    children,
    invite,
}: IAccountSetUpProviderProps): ReactElement => {
    const [currentScreen, setCurrentScreen] =
        useState<FirstLoginDispatcherKeys>(
            isInviteMode ? INVITE_INITIAL : FIRST_LOGIN_INITIAL
        )

    const value = useMemo(
        () => ({
            canClaimDomain,
            currentScreen,
            setCurrentScreen,
            invite,
            isInviteMode,
        }),
        [currentScreen, setCurrentScreen, canClaimDomain, invite, isInviteMode]
    )

    return (
        <AccountSetUpContext.Provider value={value}>
            {children}
        </AccountSetUpContext.Provider>
    )
}

AccountSetUpProvider.defaultProps = {
    invite: undefined,
}

export default AccountSetUpProvider
